import { defineStore } from "pinia";
import {
  bankInfoResponse,
  notification,
  projects,
  userPaymentTransfersResponse,
} from "../types/apiResponse";
import {
  APPLICATION_STATE,
  APPLIED_TAB_LIST,
  MEDIA_TYPE,
  rowsPerPage,
  WITHDRAWN,
} from "../consts/common";
import {
  addFavoriteProject,
  applyProject,
  cancelProject,
  deleteFavoriteProject,
  getApi,
  getAppliedProjects,
  getDocumentProjects,
  getFavoriteProjects,
  getNotification,
  getProjectDetail,
  messages,
  readNotification,
  receiveProduct,
  sendDraft,
  submitPost,
  uploadResource,
  userPaymentTransfers,
} from "../services/ProjectService";
import { signIn } from "../services/UserInfoService";
import { UserInfoStore } from "./UserInfoStore";
import { CommonStore } from "./CommonStore";
const activeProject = new Map();
const appliedProject = new Map<number, projects[]>();
const documentProject = new Array<projects>();
const favoriteProject = new Array<projects>();
const userPaymentTransfer = new Array<userPaymentTransfersResponse>();

const bankInfoResponse: bankInfoResponse = {
  id: 1, //銀行口座ID
  bank_account_name: "", //口座名義
  bank_branch: "", //支店名
  bank_code: "", //口座番号
  bank_name: "", //銀行名
  bank_type: 0, //口座種別 普通:0, 当座: 1
  account_type: 0, //種別 個人:0, 法人: 1
};

const notification: notification = {
  total: -1,
  applied: -1,
  progress: -1,
  completed: -1,
};

const allCalled: boolean[] = [false, false, false, false];
const appliedallCalled: boolean[] = [false, false, false];

export const ProjectStore = defineStore("omi", {
  state: () => {
    return {
      _active_project_list: activeProject
        .set(MEDIA_TYPE.INSTAGRAM, [])
        .set(MEDIA_TYPE.TIKTOK, [])
        .set(MEDIA_TYPE.YOUTUBE, [])
        .set(MEDIA_TYPE.X, []),
      _applied_project_list: appliedProject,
      _favorite_project_list: favoriteProject,
      _selected_tab: -1,
      _selected_status: 1,
      _selected_project_id: 0,
      _document_list: documentProject,
      /** 案件一覧ページ情報[instagram,tiktok,youtube,x] */
      _projectListPageNum: [1, 1, 1, 1],
      /** 応募タブページ情報[応募中、案件進行中、納品済] */
      _appliedProjectListPageNum: [1, 1, 1],
      _allCalled: allCalled,
      _appliedallCalled: appliedallCalled,
      _favoritePageNum: 1,
      _favoriteallCalled: false,
      _documentPageNum: 1,
      _documentallCalled: false,
      _fileUploadList: new Map<
        number,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        { url: string; display: string; file: any }[]
      >(),
      _fileUploadResult: new Array<{ id: number; url: string }>(),
      _fileAllUploaded: false,
      _notification: notification,
      // 案件一覧リクエスト管理（key:media_type, value:page）
      _activeProjectRequestManagement: new Map<number, number>()
        .set(0, 0)
        .set(1, 0)
        .set(2, 0)
        .set(3, 0),
      // お気に入り一覧リクエスト管理
      _favoriteProjectRequestManagement: 0,
      // 三条書面一覧リクエスト管理
      _documentProjectRequestManagement: 0,
      // 応募中タブ
      _appliedProjectRequestManagement: new Map<number, number>()
        .set(1, 0)
        .set(2, 0)
        .set(3, 0),
      _activeProjectFirstLoad: false,
      _isFavoriteListUpdated: false,
      _userPaymentTransfers: userPaymentTransfer,
      _draftBeforeChecked: "",
      _backToUploadFile: false,
    };
  },

  getters: {
    /** 募集中の案件一覧 */
    activeProjectList(): Map<number, projects[]> {
      return this._active_project_list;
    },

    /** お気に入り登録済の案件一覧 */
    favoriteProjectList(): projects[] {
      return this._favorite_project_list;
    },

    /** 応募中/案件進行中/納品済みの案件一覧 */
    appliedProjectList(): Map<number, projects[]> {
      return this._applied_project_list;
    },
    selectedTab(): number {
      return this._selected_tab;
    },
    selectedStatus(): number {
      return this._selected_status;
    },
    /** 募集中の案件一覧 */
    selectedId(): number {
      return this._selected_project_id;
    },
    /** 三条書面一覧 */
    documentList(): projects[] {
      return this._document_list;
    },
    /** 案件一覧ページング情報 */
    projectListPageNum(): number[] {
      return this._projectListPageNum;
    },
    /** 案件一覧取得状況（全権取得時true） */
    allCalled(): boolean[] {
      return this._allCalled;
    },
    /** 応募中タブページング情報 */
    appliedProjectListPageNum(): number {
      return this._appliedProjectListPageNum[this.selectedStatus - 1];
    },
    /** 応募中タブ取得状況（全権取得時true） */
    appliedAllCalled(): boolean[] {
      return this._appliedallCalled;
    },
    /** お気に入り一覧ページング情報 */
    favoritePageNum(): number {
      return this._favoritePageNum;
    },
    /** お気に入り一覧取得状況（全権取得時true） */
    favoriteAllCalled(): boolean {
      return this._favoriteallCalled;
    },
    /** 三条書面一覧ページング情報 */
    documentPageNum(): number {
      return this._documentPageNum;
    },
    /** 三条書面一覧取得状況（全権取得時true） */
    documentAllCalled(): boolean {
      return this._documentallCalled;
    },
    /** ファイルアップロード情報(ローカル) key:案件番号 data: blobURL */
    fileUploadList(): Map<
      number,
      {
        url: string;
        display: string;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        file: any;
        duration: string;
      }[]
    > {
      return this._fileUploadList;
    },
    /** ファイルアップロード結果 */
    fileUploadResult(): { id: number; url: string }[] {
      return this._fileUploadResult;
    },
    fileAllUploaded(): boolean {
      return this._fileAllUploaded;
    },

    /** 案件通知 */
    notification(): notification {
      return this._notification;
    },
    /** 案件一覧リクエスト管理 */
    activeProjectRequestManagement(): Map<number, number> {
      return this._activeProjectRequestManagement;
    },
    /** お気に入り一覧リクエスト管理 */
    favoriteProjectRequestManagement(): number {
      return this._favoriteProjectRequestManagement;
    },
    /** 三条書面一覧リクエスト管理 */
    documentProjectRequestManagement(): number {
      return this._documentProjectRequestManagement;
    },
    /** 応募中タブ一覧リクエスト管理 */
    appliedProjectRequestManagement(): Map<number, number> {
      return this._appliedProjectRequestManagement;
    },
    /** 案件一覧初期表示 */
    activeProjectFirstLoad(): boolean {
      return this._activeProjectFirstLoad;
    },
    /** 支払い通知書一覧 */
    userPaymentTransfers(): userPaymentTransfersResponse[] | [] {
      return this._userPaymentTransfers;
    },
    /** 前回提出分の投稿内容 */
    draftBeforeChecked(): string {
      return this._draftBeforeChecked;
    },
  },

  actions: {
    /** 案件一覧取得API */
    async getApi(media_type: number) {
      const userInfoStore = UserInfoStore();
      if (userInfoStore.userDetail.user_state == WITHDRAWN) return;
      await signIn()
        .then(async () => {
          if (
            this.activeProjectRequestManagement.get(media_type) ==
            this.projectListPageNum[media_type]
          ) {
            return;
          }
          this.setActiveProjectRequestManagement(
            media_type,
            this.projectListPageNum[media_type]
          );
          const res = await getApi(media_type);
          this.setActiveProjectList(res, media_type);
        })
        .catch((e) => {
          console.log(e);
        });
      return this._active_project_list;
    },
    /** 案件詳細取得API */
    async getProjectDetail(): Promise<projects> {
      let result!: projects;
      await signIn()
        .then(async () => {
          await getProjectDetail(this.selectedId).then((res) => {
            if (res) result = res;
          });
        })
        .catch((e) => {
          console.log(e);
        });
      return result;
    },
    /** お気に入り案件一覧 */
    async getFavoriteProjects(page?: number) {
      return await signIn()
        .then(async () => {
          if (!page) {
            if (this.favoriteProjectRequestManagement == this.favoritePageNum) {
              return;
            }
            this.setFavoriteProjectRequestManagement(this.favoritePageNum);
          }

          const result = await getFavoriteProjects(
            page ? page : this.favoritePageNum
          );
          if (!page) this.setFavoriteProjectList(result);
          else return result;
        })
        .catch((e) => {
          console.log(e);
          // return this._favorite_project_list;
        });
      // return this._favorite_project_list;
    },
    /** お気に入り案件追加
    TODO一覧APIが呼ばれているか */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    async addFavoriteProject(project: any): Promise<boolean> {
      let result = false;
      const commonStore = CommonStore();
      await signIn().then(async () => {
        result = await addFavoriteProject(project.id);
      });
      if (result) {
        this._isFavoriteListUpdated = true;
        commonStore.setToast({
          showToast: true,
          message: "お気に入りに追加しました",
          state: 0,
          timeout: 2000,
        });
        if (this.favoriteAllCalled) {
          // お気に入り案件一覧の最初に追加
          this._favorite_project_list.unshift(project);
        }
        // else if (this._favorite_project_list.length) {
        //   this.forceUpdateFavorite();
        // }
        return result;
      } else {
        commonStore.setToast({
          showToast: true,
          message: "お気に入り登録に失敗しました",
          state: 1,
          timeout: 2000,
        });
      }
      return result;
    },

    /** お気に入り解除
    お気に入り一覧全権取得済の場合はお気に入り追加削除案件はストアで処理する
    全権取得済じゃ無いかつ、取得済の案件を削除した場合は現状取得済のお気に入り一覧を再取得 */
    async deleteFavoriteProject(projectId: number): Promise<boolean> {
      let result = false;
      const commonStore = CommonStore();
      await signIn().then(async () => {
        result = await deleteFavoriteProject(projectId);
      });
      if (result) {
        this._isFavoriteListUpdated = true;
        commonStore.setToast({
          showToast: true,
          message: "お気に入りを解除しました",
          state: 0,
          timeout: 2000,
        });
        // お気に入り一覧が全件取得済かどうかチェック
        if (this.favoriteAllCalled) {
          const deleteProjectIndex = this._favorite_project_list.findIndex(
            (v) => v.id == projectId
          );
          // お気に入り一覧から削除
          if (deleteProjectIndex != undefined) {
            this._favorite_project_list.splice(deleteProjectIndex, 1);
          }
        }
        // else if (this._favorite_project_list.length) {
        //   this.forceUpdateFavorite();
        // }
        return result;
      } else {
        commonStore.setToast({
          showToast: true,
          message: "お気に入りの解除に失敗しました",
          state: 1,
          timeout: 2000,
        });
      }
      return result;
    },
    // /** 取得済のお気に入り一覧を更新する */
    // async updateFavoriteProject() {
    //   if (!this._favorite_project_list.length) return;
    //   // お気に入り案件一覧クリア
    //   this.setFavoriteProjectList([], true);
    //   for (let i = 1; i <= this.favoritePageNum; i++) {
    //     await this.getFavoriteProjects(i);
    //   }
    // },

    /** 応募中（タブ） */
    async getAppliedProjects(aplication_state: number) {
      // if (updateData) {
      //   if (
      //     this.appliedProjectList.get(this.selectedStatus) &&
      //     this.appliedProjectList.get(this.selectedStatus)!.length &&
      //     this.appliedProjectList.get(this.selectedStatus)!.length %
      //       rowsPerPage ==
      //       0
      //   ) {
      //     this.setAppliedProjectListPageNum(this.appliedProjectListPageNum + 1);
      //   }
      // }
      await signIn()
        .then(async () => {
          if (
            this.appliedProjectRequestManagement.get(aplication_state) ==
            this.appliedProjectListPageNum
          ) {
            return;
          }
          this.setAppliedProjectRequestManagement(
            aplication_state,
            this.appliedProjectListPageNum
          );
          await getAppliedProjects(
            APPLIED_TAB_LIST.get(aplication_state).state,
            this.appliedProjectListPageNum
          )
            .then((res: projects[]) => {
              if (!res.length && this.appliedProjectListPageNum == 1) {
                this.setAppliedProjectRequestManagement(aplication_state, 0);
              }
              this.setAppliedProjectList(res, aplication_state);
            })
            .then(async () => {
              // 応募済の案件のステータスが案件進行中のステータスに変わった時(進行中案件取得時に)
              let disputeItemList = [];
              this.appliedProjectList.get(2)?.forEach((v) => {
                disputeItemList =
                  this.appliedProjectList
                    .get(1)
                    ?.filter((applied) => v.id == applied.id) || [];
              });
              if (disputeItemList.length) {
                await this.forceUpdateAppliedProjects(1);
              }

              // 案件進行中のステータスが完了以降のステータスに変わった時(完了案件取得時に)
              let doneItemList = [];
              this.appliedProjectList.get(3)?.forEach((v) => {
                doneItemList =
                  this.appliedProjectList
                    .get(2)
                    ?.filter((applied) => v.id == applied.id) || [];
              });
              if (doneItemList.length) {
                this.forceUpdateAppliedProjects(2);
              }
            });
        })
        .catch((e) => {
          console.log(e);
          return this._applied_project_list;
        });
      return this._applied_project_list;
    },

    /** 応募中（タブ）FORCE UPDATE 最後の案件のページ数が変わる可能性があるため、allcalledは一旦リセットしてupdateした結果でもう一度セットする*/
    async forceUpdateAppliedProjects(aplication_state: number) {
      const commonStore = CommonStore();
      await signIn().then(async () => {
        const updateItemList: projects[] = [];
        // 最後まで呼んだかどうかは一旦リセット
        this.setAppliedAllCalled(aplication_state - 1, false);
        commonStore.setUpdatingToast({
          showToast: true,
          message: "データ更新中です",
          state: 0,
        });
        for (
          let i = 1;
          i <= this._appliedProjectListPageNum[aplication_state - 1];
          i++
        ) {
          await getAppliedProjects(
            APPLIED_TAB_LIST.get(aplication_state).state,
            i
          )
            .then((res: projects[]) => {
              // this.setAppliedProjectList(res, aplication_state);
              updateItemList.push(...res);
              // return this._applied_project_list;
            })
            .then(() => {
              this.resetAppliedProjectList(aplication_state);
              this.setAppliedProjectList(updateItemList, aplication_state);
            })

            // })
            .catch((e) => {
              console.log(e);
              return this._applied_project_list;
            });
        }

        commonStore.setUpdatingToast({
          showToast: true,
          message: "データ更新中です",
          timeout: 100,
          state: 0,
        });
      });

      return this._applied_project_list;
    },
    /** お気に入り一覧FORCE UPDATE */
    async forceUpdateFavorite() {
      if (!this._isFavoriteListUpdated) return;
      const commonStore = CommonStore();
      await signIn().then(async () => {
        let updateItemList: projects[] = [];
        commonStore.setUpdatingToast({
          showToast: true,
          message: "データ更新中です",
          state: 0,
        });
        // store格納済お気に入りクリア
        this.setFavoriteProjectList([], true);
        for (let i = 1; i <= this._favoritePageNum; i++) {
          updateItemList = [];
          await this.getFavoriteProjects(i)
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            .then((res: any) => {
              // this.setAppliedProjectList(res, aplication_state);
              updateItemList = res;
              // return this._applied_project_list;
            })
            .then(() => {
              this.setFavoriteProjectList(updateItemList);
            })

            // })
            .catch((e) => {
              console.log(e);
              return this._favorite_project_list;
            });
          commonStore.setUpdatingToast({
            showToast: false,
            message: "データ更新中です",
            state: 100,
          });
        }
        this._isFavoriteListUpdated = false;
      });

      return this._favorite_project_list;
    },

    /** 三条書面FORCE UPDATE */
    async forceUpdateDocumentProjects() {
      await signIn().then(async () => {
        const updateItemList: projects[] = [];
        // store格納済三条書面クリア
        this.setDocumentList([]);
        for (let i = 1; i <= this._documentPageNum; i++) {
          await getDocumentProjects([2, 3, 4, 5, 6, 7, 8, 9], i)
            .then((res: projects[]) => {
              // this.setAppliedProjectList(res, aplication_state);
              updateItemList.push(...res);
              // return this._applied_project_list;
            })
            .then(() => {})

            // })
            .catch((e) => {
              console.log(e);
              return this._applied_project_list;
            });
        }
        this.setDocumentList(updateItemList);
      });

      return this._applied_project_list;
    },

    /** 三条書面リスト */
    async getDocumentProjects() {
      await signIn()
        .then(async () => {
          if (this.documentProjectRequestManagement == this.documentPageNum) {
            return;
          }
          this.setDocumentProjectRequestManagement(this.documentPageNum);

          await getDocumentProjects(
            [2, 3, 4, 5, 6, 7, 8, 9],
            this.documentPageNum
          ).then((res: projects[]) => {
            if (!res.length && this.documentPageNum == 1) {
              this.setDocumentProjectRequestManagement(0);
            }
            this.setDocumentList(res);
            return this._document_list;
          });
        })
        .catch((e) => {
          console.log(e);
          return this._document_list;
        });
      return this._document_list;
    },
    /** 応募 */
    async applyProject(): Promise<boolean> {
      const commonStore = CommonStore();
      let result = false;
      await signIn()
        .then(async () => {
          await applyProject(this._selected_project_id)
            .then(async (res) => {
              if (!res.result) {
                result = false;
                commonStore.setToast({
                  showToast: true,
                  message: res.message || "案件応募に失敗しました",
                  state: 1,
                  timeout: 2000,
                });
              } else {
                // 応募中一覧(応募済)/再応募の場合は完了一覧（キャンセル済も）を更新する
                if (this.appliedProjectList.get(1)?.length) {
                  // await this.getAppliedProjects(1);
                  this.forceUpdateAppliedProjects(1);
                }
                if (this.appliedProjectList.get(3)?.length) {
                  // await this.getAppliedProjects(3);
                  this.forceUpdateAppliedProjects(3);
                }
                commonStore.setToast({
                  showToast: true,
                  message: "案件応募が完了しました",
                  state: 0,
                  timeout: 2000,
                });
                result = true;
              }
            })
            .catch((e) => {
              console.log(e);
              result = false;
            });
        })
        .catch((e) => {
          console.log(e);
          result = false;
        });
      return result;
    },

    /** 応募キャンセル */
    async cancelProject(): Promise<boolean> {
      const commonStore = CommonStore();
      let result = false;
      await signIn()
        .then(async () => {
          await cancelProject(this.selectedId)
            .then(async (res) => {
              if (!res.result) {
                commonStore.setToast({
                  showToast: true,
                  message: res.message,
                  state: 1,
                  timeout: 2000,
                });

                result = false;
              } else {
                commonStore.setToast({
                  showToast: true,
                  message: "案件をキャンセルしました",
                  state: 0,
                  timeout: 2000,
                });
                // 完了一覧 一件でも取得済であれば全件再取得
                if (
                  this.appliedProjectList.get(3)?.length ||
                  this.appliedAllCalled[2]
                ) {
                  this.forceUpdateAppliedProjects(3);
                }

                result = res.result;
              }
            })
            .catch((e) => {
              console.log(e);
            });
        })
        .catch((e) => {
          console.log(e);
        });
      return result;
    },

    /**  ファイルアップロード */
    async uploadResource(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      params: { url: string; display: string; file: any }[]
    ) {
      // if (localStorage.getItem("token")) return;
      // ファイルデータ、投稿順番
      this.setFileAllUploaded(false);
      // 結果リセット
      this.resetFileUploadResult();

      for (let i = 0; i < params.length; i++) {
        // ブラウザバックしたら処理を止める
        if (this._backToUploadFile) {
          this.setBackToUploadFile(false);
          break;
        }
        const request = new FormData();
        if (params[i].file.type.match(/video/)) {
          request.append("video_file", params[i].file);
        } else {
          request.append("file", params[i].file);
        }
        const result = await uploadResource(request, this.selectedId);
        this.setFileUploadResult(result);
      }
      if (params.length == this.fileUploadResult.length)
        this.setFileAllUploaded(true);
    },
    //  下書き送信
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    async sendDraft(params: {
      content: string;
      resources: number[];
      url: string;
    }): Promise<boolean> {
      // if (localStorage.getItem("token")) return;
      const data = params;
      const commonStore = CommonStore();
      const res = await sendDraft(data, this.selectedId);
      if (!res.result) {
        commonStore.setToast({
          showToast: true,
          message: res.message || "ステータスの更新に失敗しました",
          state: 1,
          timeout: 2000,
        });
      } else {
        // bloburl memory解放
        this.fileUploadList.get(this.selectedId)?.forEach((v) => {
          URL.revokeObjectURL(v.url);
        });
        commonStore.setToast({
          showToast: true,
          message: "ステータスの更新が完了しました",
          state: 0,
          timeout: 2000,
        });
      }
      return res.result;
    },
    /**  商品受け取り完了 */
    async receiveProduct(): Promise<boolean> {
      const commonStore = CommonStore();
      const res = await receiveProduct(this.selectedId);
      if (!res.result) {
        commonStore.setToast({
          showToast: true,
          message: res.message || "ステータスの更新に失敗しました",
          state: 1,
          timeout: 2000,
        });
      } else {
        commonStore.setToast({
          showToast: true,
          message: "ステータスの更新が完了しました",
          state: 0,
          timeout: 2000,
        });
      }
      return res.result;
    },

    /**  案件投稿完了 */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    async submitPost(param?: { url: string }): Promise<boolean> {
      const commonStore = CommonStore();
      const res = await submitPost(this.selectedId, param);
      if (!res.result) {
        commonStore.setToast({
          showToast: true,
          message: res.message || "ステータスの更新に失敗しました",
          state: 1,
          timeout: 2000,
        });
      } else {
        commonStore.setToast({
          showToast: true,
          message: "ステータスの更新が完了しました",
          state: 0,
          timeout: 2000,
        });
      }
      return res.result;
    },

    /**  案件通知 */
    async getNotification() {
      const result = await getNotification();
      if (result) {
        this.setNotification(result);
      } else {
        this.setNotification({
          total: 0,
          applied: 0,
          progress: 0,
          completed: 0,
        });
      }
    },
    /**  案件通知既読 */
    async readNotification(req: { step: number }) {
      const result = await readNotification(req);
      if (result) {
        this.setNotification(result);
      }
    },
    /**  お問い合わせ内容を管理側にも送信する */
    async messages(req: { message: string }) {
      await messages(req);
    },

    /**  支払い通知書一覧 */
    async getUserPaymentTransfers() {
      const res = await userPaymentTransfers();
      this.setUserPaymentTransfers(res);
    },
    /** setter */
    setSelectedTab(id: number) {
      this._selected_tab = id;
    },
    setSelectedStatus(id: number) {
      this._selected_status = id;
    },

    setActiveProjectList(data: projects[], mediaId: number) {
      if (data.length) {
        const tmpDataList = (
          this._active_project_list.get(mediaId) || []
        )?.concat(data);
        this._active_project_list.set(mediaId, tmpDataList);
        if (data.length < rowsPerPage) {
          this.setAllCalled(mediaId);
        }
      } else {
        this.setAllCalled(mediaId);
        this.setProjectListPageNum(
          this._projectListPageNum[mediaId] - 1,
          mediaId
        );
      }
    },
    setAppliedProjectList(data: projects[], aplication_state: number) {
      // 無限スクロールの場合は以下
      // const tmpDataList = (
      //   this._applied_project_list.get(aplication_state) || []
      // )?.concat(data);
      // this._applied_project_list.set(aplication_state, tmpDataList);
      // 変更を検知してもらえないので一旦データ削除後データ格納
      // this._applied_project_list.delete(aplication_state);
      // this._applied_project_list.set(aplication_state, data);

      /** 応募をして応募済タブを更新する場合
       * 全件取得済の場合
      10ページずつ表示してます
      案件に応募した時、キャンセルした時該当する */
      // if (updateData && data.length) {
      //   const tmpDataList = (
      //     this._applied_project_list
      //       .get(aplication_state)
      //       ?.splice(0, (this.appliedProjectListPageNum - 1) * rowsPerPage) ||
      //     []
      //   )?.concat(data);
      //   this._applied_project_list.set(aplication_state, tmpDataList);
      //   if (data.length != rowsPerPage) {
      //     this.setAppliedAllCalled(this.selectedStatus - 1);
      //   }
      // } else
      if (data.length) {
        const tmpDataList = (
          this._applied_project_list.get(aplication_state) || []
        )?.concat(data);
        // this._active_project_list.set(data[0].media_type, data);
        this._applied_project_list.set(aplication_state, tmpDataList);
        if (data.length < rowsPerPage) {
          this.setAppliedAllCalled(this.selectedStatus - 1, true);
        }
      } else {
        this.setAppliedAllCalled(this.selectedStatus - 1, true);
        this.setAppliedProjectListPageNum(this.appliedProjectListPageNum);
      }
    },
    // 案件をリセット
    resetAppliedProjectList(state: number) {
      this._applied_project_list.set(state, []);
    },
    /** お気に入り案件一覧 */
    setFavoriteProjectList(data: projects[], reset?: boolean) {
      if (reset) {
        this._favorite_project_list.splice(
          0,
          this._favorite_project_list.length
        );
        return;
      }
      if (data.length) {
        this._favorite_project_list.push(...data);
      }
      if (data.length < 10) {
        this.setFavoriteAllCalled();
      }
    },

    /** キャンセルをした案件は応募済一覧から削除する */
    deleteAppliedProjectList(data: projects[], aplication_state: number) {
      this.appliedProjectList.set(aplication_state, data);
    },
    /** キャンセルをした案件は完了一覧に追加する */
    addAppliedProjectList(data: projects[], aplication_state: number) {
      this.appliedProjectList.set(aplication_state, data);
    },
    setDocumentList(data: projects[]) {
      if (data.length != rowsPerPage) {
        this.setDocumentAllCalled();
      }
      if (data.length) {
        const tmpDataList = this._document_list.concat(data);
        // this._active_project_list.set(data[0].media_type, data);
        this._document_list = tmpDataList;
      }
    },
    setSelectedId(id: number) {
      this._selected_project_id = id;
    },
    /** 案件一覧ページング情報 */
    setProjectListPageNum(page: number, mediaId: number) {
      this._projectListPageNum[mediaId] = page;
    },
    setAllCalled(mediaId: number) {
      this._allCalled[mediaId] = true;
    },
    /** 応募タブページング情報 */
    setAppliedProjectListPageNum(page: number) {
      this._appliedProjectListPageNum[this.selectedStatus - 1] = page;
    },
    setAppliedAllCalled(selectedStatus: number, result: boolean) {
      this._appliedallCalled[selectedStatus] = result;
    },
    setFavoritePageNum(page: number) {
      this._favoritePageNum = page;
    },
    setFavoriteAllCalled() {
      this._favoriteallCalled = true;
    },
    setDocumentPageNum(page: number) {
      this._documentPageNum = page;
    },
    setDocumentAllCalled() {
      this._documentallCalled = true;
    },
    /** ローカルのファイル（ブラウザバックした時用） */
    setFileUploadList(
      projectId: number,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      url: { url: string; display: string; file: any }[]
    ) {
      this._fileUploadList.set(projectId, url);
    },
    /** ファイルアップロードした結果 */
    setFileUploadResult(data: { id: number; url: string }) {
      this._fileUploadResult.push(data);
    },
    /** ファイルアップロードした結果リセット */
    resetFileUploadResult() {
      this._fileUploadResult = [];
    },
    setFileAllUploaded(isAllUploaded: boolean) {
      this._fileAllUploaded = isAllUploaded;
    },
    /** お気に入り更新（案件一覧） */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    updateActiveProject(item: any, mediaId: number, isFavorited: boolean) {
      const targetProjectIndex = this.activeProjectList
        .get(mediaId)
        ?.findIndex((v) => v.id == item.id);
      if (targetProjectIndex == undefined) return;
      const updateData: projects =
        this.activeProjectList.get(mediaId)![targetProjectIndex];
      // お気に入り情報を更新
      if (updateData) {
        updateData.is_favorited = isFavorited;
        this.activeProjectList
          .get(mediaId)
          ?.splice(targetProjectIndex, 1, updateData);
      }
    },
    /** 未読 */
    setNotification(result: notification) {
      this._notification = result;
    },

    /** 案件一覧リクエスト管理 */
    setActiveProjectRequestManagement(key: number, value: number) {
      this._activeProjectRequestManagement.set(key, value);
    },
    /** お気に入り一覧リクエスト管理 */
    setFavoriteProjectRequestManagement(value: number) {
      this._favoriteProjectRequestManagement = value;
    },
    /** 三条書面一覧リクエスト管理 */
    setDocumentProjectRequestManagement(value: number) {
      this._documentProjectRequestManagement = value;
    },
    /** 応募中タブ一覧リクエスト管理 */
    setAppliedProjectRequestManagement(key: number, value: number) {
      this._appliedProjectRequestManagement.set(key, value);
    },
    /** 案件一覧初期表示 */
    setActiveProjectFirstLoad() {
      this._activeProjectFirstLoad = true;
    },
    setUserPaymentTransfers(res: userPaymentTransfersResponse[]) {
      this._userPaymentTransfers = res;
    },

    /** 応募ステータス更新（応募中→進行中)
     * 三条書面一覧も全件取得時はstoreにプッシュする
     */
    updateAppliedToProgressProject(detail: projects) {
      // 応募中タブが全件取得済の場合
      if (this.appliedAllCalled[0]) {
        const targetProjectList =
          this.appliedProjectList.get(1)?.filter((v) => v.id != detail.id) ||
          [];
        // if (targetProjectList.length) {
        this.resetAppliedProjectList(1);
        this.setAppliedProjectList(targetProjectList, 1);
        // }
      } else {
        // 取得、store格納済の案件一覧を削除して進行中案件取得済ページ分まで再取得
        this.forceUpdateAppliedProjects(1);
      }

      // 進行中案件一件取得済であれば再取得
      if (this.appliedProjectList.get(2)?.length) {
        this.forceUpdateAppliedProjects(2);
      }

      // 三条書面一件取得済であれば再取得FORCE UPDATE
      if (this.documentList.length) {
        this.forceUpdateDocumentProjects();
      }
    },
    /** 応募ステータス更新（進行中案件 */
    updateProgressProject() {
      // 進行中案件一件取得済であれば再取得
      if (this.appliedProjectList.get(2)?.length) {
        this.forceUpdateAppliedProjects(2);
      }
    },
    /** 応募ステータス更新（進行中案件 → 完了案件）/(応募中→完了)
     * 進行中一覧と完了一覧を更新する
     */
    updateProgressToDoneProject(state: number) {
      // 応募者が応募をキャンセルした場合、もしくは落選した場合応募案件一覧を更新する
      if (
        (state == APPLICATION_STATE.CANCELED ||
          state == APPLICATION_STATE.REJECTED) &&
        this.appliedAllCalled[0]
      ) {
        const appliedProjectList = this.appliedProjectList.get(1);

        const updateProject = appliedProjectList?.filter(
          (v) => v.id != this.selectedId
        );
        this.deleteAppliedProjectList(updateProject || [], 1);
        // 全件取得済でない場合は現ページまで再取得
      } else if (
        state == APPLICATION_STATE.CANCELED ||
        state == APPLICATION_STATE.REJECTED
      ) {
        // TODO ↓呼び出しに時間がかかるのでストア一旦削除

        const appliedProjectList = this.appliedProjectList.get(1);

        const updateProject = appliedProjectList?.filter(
          (v) => v.id != this.selectedId
        );
        this.deleteAppliedProjectList(updateProject || [], 1);
        // TODO ↑v呼び出しに時間がかかるのでストア一旦削除

        this.forceUpdateAppliedProjects(1);
      }

      // 進行中案件一件取得済であれば再取得
      if (this.appliedProjectList.get(2)?.length) {
        this.forceUpdateAppliedProjects(2);
      }
      // 完了案件一件取得済であれば再取得
      if (this.appliedProjectList.get(3)?.length) {
        this.forceUpdateAppliedProjects(3);
      }
    },
    /** 応募ステータス更新（応募中→進行中)
     * 三条書面一覧も全件取得時かつ、更新済でなければstoreにプッシュする
     */
    updateDocumentsProject(detail: projects) {
      // 三条書面一覧を更新する(全件取得時)
      if (this.documentAllCalled) {
        // ストア更新済でなければ
        if (!this.documentList.find((v) => v.id == detail.id)) {
          this.setDocumentList([detail]);
        }
      }
    },
    // 下書きの内容(前回提出分)を保存しておく
    setDraftBeforeChecked(content: string) {
      this._draftBeforeChecked = content;
    },
    // 下書き送信画面でファイルアプロード中にブラウザバックしてファイルアップロード画面に戻った場合、
    setBackToUploadFile(e: boolean) {
      this._backToUploadFile = e;
    },
  },
});
